import React from 'react';
import { graphql, useFragment, useMutation } from 'utils/graphClient';
import { Text } from '@cardboard-ui/react';
import { PublishDialog } from 'components/PublishBar/PublishDialog';
import { t } from '@lingui/macro';
import { RemirrorJSON } from 'remirror';
import { OverlayProps } from '../../TaskOverlay';
import { PublishFolderItemDialog_folderItem$key } from './__generated__/PublishFolderItemDialog_folderItem.graphql';
import { PublishFolderItemDialog_publishFolderNodesMutation } from './__generated__/PublishFolderItemDialog_publishFolderNodesMutation.graphql';
import { PublishFolderItemDialog_updateQuestion_Mutation } from './__generated__/PublishFolderItemDialog_updateQuestion_Mutation.graphql';

const folderItemFragment = graphql`
  fragment PublishFolderItemDialog_folderItem on FolderItem {
    id
    name
    content {
      __typename
      ... on Page {
        id
        data
      }
      ... on Form {
        id
        questions {
          id
          definition {
            __typename
            id
            ... on CustomField__Checkboxes {
              options {
                text
              }
            }
            ... on CustomField__MultipleChoice {
              options {
                text
              }
            }
          }
        }
      }
    }
    space {
      id
    }
  }
`;

const publishFolderNodesMutation = graphql`
  mutation PublishFolderItemDialog_publishFolderNodesMutation(
    $folderNodeId: ID!
    $createAnnouncement: Boolean!
    $spaceId: ID!
    $message: String!
    $announcementConnections: [ID!]!
    $updateName: Boolean!
    $newNodeName: String!
  ) {
    publishFolderNodes(input: { folderNodeIds: [$folderNodeId] }) {
      folderNodesPublished {
        ...FolderNodePublishBar_folderNode
      }
    }
    createAnnouncement(
      input: { spaceId: $spaceId, message: $message, linkId: $folderNodeId }
    ) @include(if: $createAnnouncement) {
      announcementCreated
        @prependNode(
          connections: $announcementConnections
          edgeTypeName: "Announcement"
        ) {
        id
        ...AnnouncementCard_announcement
      }
    }
    updateFolderNode(
      input: { folderNodeId: $folderNodeId, name: $newNodeName }
    ) @include(if: $updateName) {
      folderNodeUpdated {
        ... on FolderNodeInterface {
          name
        }
        ... on Node {
          id
        }
      }
    }
  }
`;

const updateQuestionMutation = graphql`
  mutation PublishFolderItemDialog_updateQuestion_Mutation(
    $input: FormsUpdateQuestionMutationInput!
  ) {
    updateQuestion(input: $input) {
      question {
        ...EditQuestionCard_formQuestion
      }
    }
  }
`;

interface PublishFolderItemDialogProps extends OverlayProps {
  folderItem: PublishFolderItemDialog_folderItem$key;
}

export function PublishFolderItemDialog({
  isOpen,
  onClose,
  folderItem,
}: PublishFolderItemDialogProps) {
  const node = useFragment(folderItemFragment, folderItem);
  const isForm = node.content?.__typename === 'Form';

  const [publishFolderNode, isPublishingLoading] =
    useMutation<PublishFolderItemDialog_publishFolderNodesMutation>(
      publishFolderNodesMutation,
    );

  const [updateQuestion] =
    useMutation<PublishFolderItemDialog_updateQuestion_Mutation>(
      updateQuestionMutation,
    );

  const onPublish = async (
    announcementMessage?: RemirrorJSON,
    newNodeName?: string,
  ) => {
    const announcementAsString = JSON.stringify(announcementMessage);
    if (!node.id) return;

    if (isForm) {
      await removeEmptyFormQuestionOptions();
    }

    publishFolderNode({
      variables: {
        folderNodeId: node.id,
        createAnnouncement: !!announcementAsString,
        message: announcementAsString || '',
        spaceId: node.space?.id || '',
        announcementConnections: [],
        updateName: !!newNodeName && newNodeName !== node.name,
        newNodeName: newNodeName || '',
      },
      onCompleted: () => {
        onClose();
      },
      updater: (store) => {
        const currentNode = store.get(node.id);

        if (!currentNode) return;

        currentNode.setValue(newNodeName, 'name');
      },
    });
  };

  const removeEmptyFormQuestionOptions = () => {
    if (isForm) {
      const promises = node.content?.questions.map((question) => {
        return new Promise((resolve, reject) => {
          const isCheckboxes =
            question.definition?.__typename === 'CustomField__Checkboxes';
          const isMultipleChoice =
            question.definition?.__typename === 'CustomField__MultipleChoice';

          if (isCheckboxes || isMultipleChoice) {
            const isEmptyOption = question.definition.options?.some(
              (option) => option.text === '',
            );

            if (isEmptyOption) {
              const withoutEmptyOptions =
                question.definition.options?.filter(
                  (option) => option.text !== '',
                ) || [];

              updateQuestion({
                variables: {
                  input: {
                    id: question.id,
                    ...(isCheckboxes && {
                      checkboxesDefinition: {
                        options: withoutEmptyOptions,
                      },
                    }),

                    ...(isMultipleChoice && {
                      multipleChoiceDefinition: {
                        options: withoutEmptyOptions,
                      },
                    }),
                  },
                },
                onCompleted: resolve,
                onError: reject,
              });
            } else {
              resolve(true);
            }
          } else {
            resolve(true);
          }
        });
      });

      return Promise.all(promises);
    }
    return Promise.resolve();
  };

  if (!node.id || !node.space) return null;

  return (
    <PublishDialog
      nodeId={node.id}
      isOpen={isOpen}
      onClose={onClose}
      title={isForm ? t`Publish form` : t`Publish page`}
      isPublishing={isPublishingLoading}
      onPublish={onPublish}
      spaceId={node.space.id}
      linkIds={[node.id]}
    >
      <Text>
        {isForm
          ? t`People who can access this form's folder will be able to fill in your form once published.`
          : t`People who can access this page's folder will be able to see your page once published.`}
      </Text>
    </PublishDialog>
  );
}
